import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  LunarAuditFindingDto,
  LunarAuditFindingsResponseDto,
  LunarAuditRunDto,
  LunarAuditRunsResponseDto,
  LunarBalancesResponseDto,
  LunarOverviewResponseDto,
  LunarPaymentsResponseDto,
  LunarReceiptDto,
  LunarReceiptsResponseDto,
  PromoHolderDto,
  PromoOverviewResponseDto,
} from '@lunary/api-client'
import { api } from '../../shared/api/client'
import { addDays, moscowToday } from '../../shared/lib/moscow-date'

export const PERIODS = [7, 30, 90] as const
export type PeriodDays = (typeof PERIODS)[number]
export const BALANCES_PAGE = 20
export const LUNARS_KEY = ['admin-lunars'] as const
export const AUDIT_RUNS_KEY = [...LUNARS_KEY, 'audit-runs'] as const

function range(days: PeriodDays): { from: string; to: string } {
  const to = moscowToday()
  return { from: addDays(to, 1 - days), to }
}

export function useLunarOverview(days: PeriodDays, enabled: boolean) {
  return useQuery({
    queryKey: [...LUNARS_KEY, 'overview', days],
    queryFn: () => api.get<LunarOverviewResponseDto>('/admin/lunars/overview', { query: range(days) }),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useLunarPayments(days: PeriodDays, enabled: boolean) {
  return useQuery({
    queryKey: [...LUNARS_KEY, 'payments', days],
    queryFn: () => api.get<LunarPaymentsResponseDto>('/admin/lunars/payments', { query: range(days) }),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useLunarBalances(flagged: boolean, offset: number) {
  return useQuery({
    queryKey: [...LUNARS_KEY, 'balances', flagged, offset],
    queryFn: () =>
      api.get<LunarBalancesResponseDto>('/admin/lunars/balances', { query: { flagged: String(flagged), limit: BALANCES_PAGE, offset } }),
    staleTime: 60_000,
    retry: false,
  })
}

export function useAuditRuns(enabled = true) {
  return useQuery({
    queryKey: AUDIT_RUNS_KEY,
    queryFn: () => api.get<LunarAuditRunsResponseDto>('/admin/lunars/audit/runs'),
    enabled,
    refetchInterval: 120_000,
    retry: false,
  })
}

export function useAuditFindings() {
  return useQuery({
    queryKey: [...LUNARS_KEY, 'audit-findings'],
    queryFn: () => api.get<LunarAuditFindingsResponseDto>('/admin/lunars/audit/findings', { query: { limit: 100 } }),
    retry: false,
  })
}

export function useRunAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<LunarAuditRunDto>('/admin/lunars/audit/runs'),
    onSuccess: () => qc.invalidateQueries({ queryKey: LUNARS_KEY }),
  })
}

export function useSetFindingStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; status: 'open' | 'seen' }) =>
      api.patch<LunarAuditFindingDto>(`/admin/lunars/audit/findings/${v.id}`, { status: v.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: LUNARS_KEY }),
  })
}

export function useRefundPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/admin/lunars/payments/${id}/refund`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LUNARS_KEY }),
  })
}

export const RECEIPTS_PAGE = 50
export const RECEIPTS_KEY = [...LUNARS_KEY, 'receipts'] as const

export function useReceipts(view: 'todo' | 'done', offset: number, enabled = true) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, view, offset],
    queryFn: () => api.get<LunarReceiptsResponseDto>('/admin/lunars/receipts', { query: { view, limit: RECEIPTS_PAGE, offset } }),
    enabled,
    refetchInterval: 60_000,
    retry: false,
  })
}

export function useIssueReceipt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; url: string }) => api.post<LunarReceiptDto>(`/admin/lunars/receipts/${v.id}/issue`, { receipt_url: v.url }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  })
}

export function useCancelReceipt() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<LunarReceiptDto>(`/admin/lunars/receipts/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  })
}

export const PROMO_PAGE = 30
const PROMO_KEY = [...LUNARS_KEY, 'promo'] as const

export function usePromo(offset: number) {
  return useQuery({
    queryKey: [...PROMO_KEY, offset],
    queryFn: () => api.get<PromoOverviewResponseDto>('/admin/lunars/promo', { query: { limit: PROMO_PAGE, offset } }),
    staleTime: 30_000,
    retry: false,
  })
}

export function useAdjustPromo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { userId: string; amount: number; comment: string }) =>
      api.post<PromoHolderDto>('/admin/lunars/promo/adjustments', { user_id: v.userId, amount: v.amount, comment: v.comment || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROMO_KEY }),
  })
}
